import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import React, { PropsWithChildren } from 'react';

import { IPeriodicJobs } from '@/modules/periodicJobs/types';
import { Typography } from '@/shared/components/atoms';

function removeDuplicatesByKey<T>(array: T[], key: keyof T): T[] {
  const uniqueKeys = new Set<T[keyof T]>();
  const resultArray: T[] = [];

  for (const obj of array) {
    if (!uniqueKeys.has(obj[key])) {
      uniqueKeys.add(obj[key]);
      resultArray.push(obj);
    }
  }

  return resultArray;
}

export const getDynamicFertilizersColumns = (
  data?: IPeriodicJobs.JobInstance[]
) => {
  if (!data) return [];

  const fertilizersData: IPeriodicJobs.DynamicFertilizer[] = [];

  for (const jobItem of data) {
    for (const fertilizer of jobItem.fertilizersUsed) {
      const name = fertilizer.name ? fertilizer.name.trim() : undefined;

      if (fertilizer.name) {
        const unit =
          typeof fertilizer.unit === 'number' ? '' : fertilizer.unit.trim();

        fertilizersData.push({
          name,
          unit,
          id: fertilizer.id,
          nutrients: fertilizer.nutrients,
        });
      }
    }
  }

  return removeDuplicatesByKey(fertilizersData, 'id');
};

const formatDate = (date?: string, locale?: string): string | null => {
  if (!date) return null;
  return new Date(date).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

const formatTime = (date?: string, locale?: string): string | null => {
  if (!date) return null;
  return new Date(date).toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
};

const getDateDifference = (
  startDate?: string,
  endDate?: string
): string | null => {
  if (!startDate || !endDate) return null;

  const date1 = new Date(startDate);
  const date2 = new Date(endDate);

  const timeDifferenceInMilliseconds = date2.getTime() - date1.getTime();
  const timeDifferenceInHours = timeDifferenceInMilliseconds / (1000 * 60 * 60);

  if (Number.isInteger(timeDifferenceInHours)) {
    return timeDifferenceInHours.toString();
  }

  return timeDifferenceInHours?.toFixed(2);
};

const getRenderedCellValue = (value: string | number | undefined | null) => (
  <Typography
    weight='medium'
    variant='subtitle2'
    className=' mx-auto max-w-[9.25rem] overflow-clip truncate text-ellipsis whitespace-nowrap'
  >
    {/* check if number and float make fixed 2 else show value oring with (-) */}
    {typeof value == 'number'
      ? Number.isInteger(value)
        ? value
        : value?.toFixed(2)
      : value || '-'}
  </Typography>
);

const Header: React.FC<PropsWithChildren> = React.memo(({ children }) => {
  return <span className='whitespace-nowrap font-bold'>{children}</span>;
});

const FertilizerHeader: React.FC<{
  fertilizer: IPeriodicJobs.DynamicFertilizer;
}> = React.memo(({ fertilizer }) => {
  return (
    <>
      <span className='whitespace-nowrap font-bold'>{fertilizer.name}</span>
      <br />
      <div className='flex flex-row flex-nowrap items-center justify-center'>
        {fertilizer.nutrients?.map((nutrient, idx) => (
          <span
            key={`${nutrient.id}-${idx}`}
            className='mx-2 whitespace-nowrap text-12'
          >
            {nutrient.nutrient} {nutrient.quantity}%
          </span>
        ))}
      </div>
    </>
  );
});

const columnHelper = createColumnHelper<IPeriodicJobs.JobInstance>();

export const jobInstancesTableColumns = (
  t: TFunction<'common', undefined>,
  language: string,
  fertilizersData: IPeriodicJobs.DynamicFertilizer[]
) => {
  const column = [];

  const locale = language === 'ar' ? 'ar-EG' : 'en-US';

  column.push(
    columnHelper.accessor('startDate', {
      cell: ({ getValue }) =>
        getRenderedCellValue(formatDate(getValue(), locale)),
      header: () => <Header>{t('date')}</Header>,
    })
  );
  column.push(
    columnHelper.accessor('code', {
      cell: ({ getValue }) =>
        getRenderedCellValue(getValue() ? `#${getValue()}` : getValue()),
      header: () => <Header>{t('jobOrderId')}</Header>,
    })
  );
  column.push(
    columnHelper.accessor('plots', {
      cell: ({ getValue }) =>
        getValue()
          .map((plot) => plot.name)
          .join(','),
      header: () => <Header>{t('work-locations')}</Header>,
    })
  );
  column.push(
    columnHelper.accessor('plots', {
      cell: ({ getValue }) =>
        getValue().reduce((acc, curr) => acc + curr.area, 0),
      header: () => (
        <Header>
          <span className='flex flex-row flex-nowrap gap-x-1'>
            {t('area')}
            <span className='font-medium'>({t('acres')})</span>
          </span>
        </Header>
      ),
    })
  );
  column.push(
    columnHelper.accessor('crop.id', {
      cell: () => getRenderedCellValue(t('nutrition')),
      header: () => <Header>{t('operrationCause')}</Header>,
    })
  );
  column.push(
    columnHelper.accessor('applicationMethod', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => <Header>{t('applicationMethod')}</Header>,
    })
  );

  for (let i = 0; i < fertilizersData.length; i++) {
    const fertilizer = fertilizersData[i];
    column.push(
      columnHelper.accessor('fertilizersUsed', {
        cell: ({ getValue }) => {
          const fertilizerUsed = getValue().find((f) => f.id === fertilizer.id);
          if (fertilizerUsed) {
            return getRenderedCellValue(
              `${fertilizerUsed.total} ${fertilizerUsed.unit}`
            );
          }
          return getRenderedCellValue(undefined);
        },
        header: () => <FertilizerHeader fertilizer={fertilizer} />,
      })
    );
  }

  column.push(
    columnHelper.accessor('startDate', {
      cell: ({ getValue }) =>
        getRenderedCellValue(formatTime(getValue(), locale)),
      header: () => <Header>{t('startingTime')}</Header>,
    })
  );
  column.push(
    columnHelper.accessor('endDate', {
      cell: ({ getValue }) =>
        getRenderedCellValue(formatTime(getValue(), locale)),
      header: () => <Header>{t('endingTime')}</Header>,
    })
  );
  column.push(
    columnHelper.accessor('startDate', {
      cell: ({ getValue, cell }) =>
        getRenderedCellValue(
          `${getDateDifference(getValue(), cell.row.original.endDate)} ${t(
            'hour'
          )} `
        ),
      header: () => <Header>{t('totalHours')}</Header>,
    })
  );
  column.push(
    columnHelper.accessor('supervisor', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => <Header>{t('supervisor')}</Header>,
    })
  );
  column.push(
    columnHelper.accessor('notes', {
      cell: ({ getValue }) => getRenderedCellValue(getValue()),
      header: () => <Header>{t('supervisorNotes')}</Header>,
    })
  );

  return column;
};
